import {
  AdditionalRequirementsPaymentProps,
  AdditionalRequirementsPaymentSchema,
  Button,
  CloseIcon,
  Modal,
  Select,
  TextArea,
  TextInput
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { useEffect } from "react";
import { UseFormReset, useForm } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import { useSelector } from "react-redux";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitPaymentDetails: (
    data: AdditionalRequirementsPaymentProps,
    reset: UseFormReset<AdditionalRequirementsPaymentProps>
  ) => void;
  remove: (id: number) => void;
  editData?: Partial<AdditionalRequirementsPaymentProps> & { id: number };
}

const defaultValues = {
  payor: "",
  payee: "",
  other_person_entity: "",
  value: "",
  description: ""
};

export default function AddPayment({ isOpen, onClose, onSubmitPaymentDetails, remove, editData }: IProps) {
  const { initialDetails } = useSelector(selectConsentOrdersBase);

  const { handleSubmit, control, watch, reset, resetField } = useForm<AdditionalRequirementsPaymentProps>({
    mode: "all",
    defaultValues,
    resolver: yupResolver(AdditionalRequirementsPaymentSchema)
  });

  const payorOptions = [
    { label: `You (${initialDetails?.user?.first_name})`, value: "user" },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" }
  ];

  const payeeOptions = [
    { label: `You (${initialDetails?.user?.first_name})`, value: "user" },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" },
    {
      label: "Someone Else",
      value: "something_else"
    }
  ];
  const isDeleting = false;
  const isLoading = false;

  const onDelete = () => {
    if (editData) {
      remove(editData.id);
    }
    onClose();
    reset();
  };

  useEffect(() => {
    if (editData) reset(editData);
    else reset(defaultValues);
  }, [editData]);

  return (
    <Modal title="Add Payment Details" isOpen={isOpen} closeModal={onClose}>
      <form
        id="payment-details-form"
        className="mt-5 space-y-5"
        onSubmit={handleSubmit(data => onSubmitPaymentDetails(data, reset))}
      >
        <Select
          title="Who will make the payment?"
          control={control}
          name="payor"
          id="payor"
          placeholder="Select"
          options={payorOptions}
        />

        <Select
          title="Who will receive the payment?"
          control={control}
          name="payee"
          id="payee"
          placeholder="Select"
          options={payeeOptions}
          onChange={val => {
            if (val !== "something_else") {
              resetField("other_person_entity", { defaultValue: "" });
            }
          }}
        />

        {watch("payee") === "something_else" ? (
          <TextInput
            title="Name of other person or entity"
            control={control}
            name="other_person_entity"
            id="other_person_entity"
          />
        ) : null}

        <TextArea title="Description" control={control} name="description" id="description" placeholder="Description" />

        <TextInput title="Amount" control={control} name="value" id="value" placeholder="eg $500,000" isAmount />

        <div className="flex justify-between items-center w-full mt-7">
          {typeof editData?.id === "number" ? (
            <button type="button" onClick={onDelete} className="h-10 text-red-500 flex justify-center items-center">
              <CloseIcon classes="fill-current text-red-500 mr-2" width={20} height={20} />
              Delete
              {isDeleting ? <Oval width={14} height={14} color="#fff" strokeWidth={5} wrapperClass="ml-2" /> : null}
            </button>
          ) : null}
          <Button type="submit" className="h-10 px-12 flex justify-center items-center mx-auto mt-10">
            Save
            {isLoading ? <Oval width={14} height={14} color="#fff" wrapperClass="ml-2" strokeWidth={5} /> : null}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
